var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useCallback, useEffect, } from "react";
import { classes } from "../utils/classes";
function ConditionalTiltCard(_a) {
    var children = _a.children, _b = _a.disabled, disabled = _b === void 0 ? false : _b, maxTilt = _a.maxTilt, glareMaxOpacity = _a.glareMaxOpacity, className = _a.className, ref = _a.ref, extras = __rest(_a, ["children", "disabled", "maxTilt", "glareMaxOpacity", "className", "ref"]);
    if (disabled) {
        return (_jsx("div", __assign({ ref: ref, className: className }, extras, { children: children })));
    }
    return (_jsx(TiltCard, __assign({ ref: ref, maxTilt: maxTilt, glareMaxOpacity: glareMaxOpacity, className: className }, extras, { children: children })));
}
// Light reflection angle based on tilt
// Simulate light source coming from top-left (more natural)
var lightSource = { x: -0.3, y: -0.5 };
function TiltCard(_a) {
    var children = _a.children, _b = _a.maxTilt, maxTilt = _b === void 0 ? 12 : _b, _c = _a.glareMaxOpacity, glareMaxOpacity = _c === void 0 ? 0.4 : _c, className = _a.className, ref = _a.ref, extras = __rest(_a, ["children", "maxTilt", "glareMaxOpacity", "className", "ref"]);
    var cardRef = useRef(null);
    var callbackRef = useCallback(function (node) {
        if (ref) {
            if (typeof ref === "function")
                ref(node);
            else
                ref.current = node;
        }
        cardRef.current = node;
    }, [ref]);
    var _d = useState({}), tiltStyle = _d[0], setTiltStyle = _d[1];
    var _e = useState({}), glareStyle = _e[0], setGlareStyle = _e[1];
    var _f = useState(false), isHovered = _f[0], setIsHovered = _f[1];
    var _g = useState(""), tiltTransition = _g[0], setTiltTransition = _g[1];
    var transitionTimeout = useRef(null);
    useEffect(function () { return function () {
        if (transitionTimeout.current) {
            clearTimeout(transitionTimeout.current);
        }
    }; }, []);
    var handleMouseMove = useCallback(function (e) {
        if (!cardRef.current)
            return;
        var rect = cardRef.current.getBoundingClientRect();
        var centerX = rect.left + rect.width / 2;
        var centerY = rect.top + rect.height / 2;
        var mouseX = (e.clientX - centerX) / (rect.width / 2);
        var mouseY = (e.clientY - centerY) / (rect.height / 2);
        var clampedMouseX = Math.max(-1.5, Math.min(1.5, mouseX));
        var clampedMouseY = Math.max(-1.5, Math.min(1.5, mouseY));
        var tiltX = clampedMouseY * maxTilt;
        var tiltY = clampedMouseX * maxTilt * -1;
        setTiltStyle({
            transform: "perspective(1000px) rotateX(".concat(tiltX, "deg) rotateY(").concat(tiltY, "deg) scale3d(1.02, 1.02, 1.02)"),
            transformOrigin: "50% 50%",
        });
        // Calculate reflection vector based on surface normal (tilt)
        var normalX = (tiltY / maxTilt) * 0.1; // Surface normal X based on Y tilt
        var normalY = (-tiltX / maxTilt) * 0.1; // Surface normal Y based on X tilt
        // Reflection calculation: reflected = incident - 2 * (incident · normal) * normal
        var dotProduct = lightSource.x * normalX + lightSource.y * normalY;
        var reflectedX = lightSource.x - 2 * dotProduct * normalX;
        var reflectedY = lightSource.y - 2 * dotProduct * normalY;
        // Position glare based on reflection angle
        var glareX = 50 + reflectedX * 40 + mouseX * 15;
        var glareY = 50 + reflectedY * 40 + mouseY * 15;
        // Calculate distance from center for intensity
        var distanceFromCenter = Math.sqrt(mouseX * mouseX + mouseY * mouseY);
        var normalizedDistance = Math.min(distanceFromCenter, 1.2);
        // More realistic opacity calculation
        var angleFactor = Math.abs(tiltX) + Math.abs(tiltY);
        var baseOpacity = Math.max(0, (angleFactor / maxTilt) * 0.7);
        var distanceOpacity = Math.pow(1 - normalizedDistance / 1.2, 1.5);
        var glareOpacity = baseOpacity * distanceOpacity * glareMaxOpacity;
        // Dynamic glare size based on tilt angle
        var tiltMagnitude = Math.sqrt(tiltX * tiltX + tiltY * tiltY);
        var sizeMultiplier = 1 + (tiltMagnitude / maxTilt) * 0.8;
        var glareSize = 40 * sizeMultiplier;
        // Create multiple layers for more realistic light reflection
        var primaryGlare = "radial-gradient(ellipse ".concat(glareSize * 1.8, "% ").concat(glareSize * 0.6, "% at ").concat(glareX, "% ").concat(glareY, "%,\n        rgba(255, 255, 255, ").concat(glareOpacity * 0.8, ") 0%,\n        rgba(255, 255, 255, ").concat(glareOpacity * 0.4, ") 30%,\n        transparent 70%)");
        var secondaryGlare = "radial-gradient(ellipse ".concat(glareSize * 3, "% ").concat(glareSize * 1.2, "% at ").concat(glareX, "% ").concat(glareY, "%,\n        transparent 0%,\n        rgba(255, 255, 255, ").concat(glareOpacity * 0.15, ") 40%,\n        transparent 80%)");
        // Add subtle color tinting for more realism
        var colorGlare = "radial-gradient(ellipse ".concat(glareSize * 1.2, "% ").concat(glareSize * 0.4, "% at ").concat(glareX, "% ").concat(glareY, "%,\n        rgba(200, 230, 255, ").concat(glareOpacity * 0.3, ") 0%,\n        transparent 60%)");
        setGlareStyle({
            background: "".concat(primaryGlare, ", ").concat(secondaryGlare, ", ").concat(colorGlare),
            opacity: glareOpacity > 0.02 ? 1 : glareOpacity / 0.02,
        });
    }, [maxTilt, glareMaxOpacity]);
    var setTiltTransitionWithTimeout = useCallback(function (transition) {
        setTiltTransition(transition);
        if (transitionTimeout.current)
            clearTimeout(transitionTimeout.current);
        transitionTimeout.current = setTimeout(function () {
            setTiltTransition("duration-0 ease-out");
        }, 300);
    }, []);
    var handleMouseEnter = useCallback(function () {
        setIsHovered(true);
        setTiltTransitionWithTimeout("duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]");
    }, [setTiltTransitionWithTimeout]);
    var handleMouseLeave = useCallback(function () {
        setIsHovered(false);
        setTiltTransitionWithTimeout("duration-300 ease-out");
        setTiltStyle({
            transform: "perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)",
        });
        setGlareStyle({
            background: "none",
            opacity: 0,
        });
    }, [setTiltTransitionWithTimeout]);
    return (_jsxs("div", __assign({ ref: callbackRef, className: classes("overflow-y-clip transition-all will-change-transform", tiltTransition, className), style: tiltStyle, onMouseEnter: handleMouseEnter, onMouseMove: isHovered ? handleMouseMove : undefined, onMouseLeave: handleMouseLeave }, extras, { children: [children, _jsx("div", { className: "pointer-events-none absolute inset-0 overflow-hidden", style: glareStyle })] })));
}
export default ConditionalTiltCard;
